import { AutogenLayoutCustomerServiceToolPanel } from "./autogen/AutogenLayoutCustomerServiceToolPanel";
import {
    Button, ClickEvent, Component, ContextMenuCreationEvent, ContextMenuItemCreator, ContextMenuLabelProps, DataSourceMode,
    DataSourceRemoteDataChangeEvent, ModelDataChangeType, Table, TableRow, TableSelectionEvent, Toast
} from "@mcleod/components";
import { CustomerService } from "./CustomerService";
import { Api, GeneralSettings, ModelRow, StringUtil } from "@mcleod/core";
import { DispatchUtil } from "../../DispatchUtil";
import { P44ShipmentTracking } from "@mcleod/powerbroker";
import { DispatchSlideout } from "../../DispatchSlideout";
import { CommonContextMenuItems, getDispatchControlValue, LMEGeneralSettings, OrderHoldAction } from "@mcleod/general";
import { Dispatch } from "../../asset/Dispatch";
import { ModelEquipmentGroup } from "../../models/ModelEquipmentGroup";
import { EquipmentGroupValidation, EquipmentGroupValidationResult } from "../../asset/EquipmentGroupValidation";
import { OrderPostHist, OrderPostHistProps } from "../../OrderPostHist";
import { isSpotOrder } from "../../Orders";
import { OrderTabView } from "../../OrderTabView";

export class CustomerServiceToolPanel extends AutogenLayoutCustomerServiceToolPanel {
    private table: Table;
    private validationResult: EquipmentGroupValidationResult;
    private tableSelectedPromise: Promise<void>;
    // right-click asynchronous options
    private buttonDispatchRightClick: Component;

    private onHoldAction: OrderHoldAction = new OrderHoldAction();

    override onLoad(): void {
        this.addMountListener(() => {
            if (this.owner instanceof CustomerService) {
                this.initialize(this.owner)
            }
        });
    }

    private getAllVisibleButtons(): Button[] {
        return this.panelButtons?.findComponentsByType(Button)?.filter(button => button.visible);
    }

    initialize(layout: CustomerService) {
        this.table = layout.tableCustomerServiceMovements;
        if (this.getAllVisibleButtons()?.length > 0) {
            this.table?.addSelectionListener((event: TableSelectionEvent) => {
                this.tableSelectedPromise = this.tableMovementsOnSelect(event);

            });
            this.table?.dataSource?.addRemoteDataChangeListener((event: DataSourceRemoteDataChangeEvent) => this.onRemoteDataChange(event));
        } else {
            this.removeAll();
        }
    }

    onRemoteDataChange(event: DataSourceRemoteDataChangeEvent) {
        if (event.change.type === ModelDataChangeType.DELETE) {
            const movementId = event.change.data.get("movement_id");
            if (movementId != null && this.table.selectedRow != null) {
                const selectedMoveId = this.table.selectedRow.data.get("movement_id");
                if (movementId === selectedMoveId)
                    this.table.selectedRow = null;
            }
        }
    }

    async tableMovementsOnSelect(event: TableSelectionEvent) {
        const selectedRows = this.table.selectedRows;
        await this.syncToolPanel(selectedRows);
    }

    public async syncToolPanel(selectedRows: TableRow[]) {
        this.getAllVisibleButtons().forEach(button => {
            button.disabledTooltip = null;
            button.enabled = selectedRows != null && selectedRows.length > 0;
            if (button.enabled) {
                this.syncButtonState(button, selectedRows);
            }
        });

        await this.syncAssignAndDispatchButtons(selectedRows);
    }

    private syncButtonState(button: Button, selectedRows: TableRow[]) {
        if (button === this.buttonReleaseToBilling) {
            this.syncButtonReleaseToBilling(selectedRows);
        } else if (button === this.buttonOrderHistory) {
            this.syncButtonOrderHistory(selectedRows);
        } else if (button === this.buttonHold) {
            this.syncButtonHold(selectedRows);
        } else if (button === this.buttonRateOrders) {
            CommonContextMenuItems.syncButtonRateOrders(button, selectedRows);
        } else if (button === this.buttonUpdateField) {
            CommonContextMenuItems.syncButtonUpdateField(button, selectedRows);
        }
    }

    private async syncAssignAndDispatchButtons(selectedRows: TableRow[]) {
        if (selectedRows.length !== 1) {
            this.buttonDispatch.enabled = false;
            this.buttonDispatch.disabledTooltip = "Please select a single order to dispatch.";
            return;
        }
        const modelRow = selectedRows[0].data;
        if (modelRow?.getBoolean("brokerage", false)) {
            await DispatchUtil.isAssignable(modelRow, { btnAssign: null, btnDispatch: this.buttonDispatch });
        } else {
            this.buttonDispatch.enabled = await this.dispatchButtonEnabled(this.table.selectedRow);
        }
        if (this.buttonDispatchRightClick != null && this.buttonDispatchRightClick.isMounted) {
            this.buttonDispatchRightClick.visible = this.buttonDispatch.enabled && this.buttonDispatch.visible;
        }
    }

    private syncButtonReleaseToBilling(selectedRows: TableRow[]) {
        if (selectedRows.length !== 1) {
            this.buttonReleaseToBilling.disabledTooltip = "Please select a single order to release to invoicing.";
            this.buttonReleaseToBilling.enabled = false;
            return;
        }
        const modelRow = selectedRows[0].data;
        if (isSpotOrder(modelRow)) {
            this.buttonReleaseToBilling.disabledTooltip = "Release to invoicing is not available for spot orders.";
            this.buttonReleaseToBilling.enabled = false;
        }
        if (!["D"].includes(modelRow.getString("move_status"))) {
            this.buttonReleaseToBilling.disabledTooltip = "Release to invoicing is only available for delivered orders.";
            this.buttonReleaseToBilling.enabled = false;
        }
        if (["D"].includes(modelRow.getString("move_status")) && modelRow.getBoolean("ready_to_bill", false)) {
            this.buttonReleaseToBilling.disabledTooltip = "This order has already been released to invoicing.";
            this.buttonReleaseToBilling.enabled = false;
        }
    }

    syncButtonOrderHistory(selectedRows: TableRow[]) {
        if (selectedRows.length !== 1) {
            this.buttonOrderHistory.disabledTooltip = "Please select a single order to view order history.";
            this.buttonOrderHistory.enabled = false;
            return;
        }
    }

    private syncButtonHold(selectedRows: TableRow[]) {
        if (!this.onHoldAction.validateSelection(selectedRows.map(row => row.data as ModelRow))) {
            this.buttonHold.disabledTooltip = "Invalid Selection";
            this.buttonHold.enabled = false;
        }
    }

    /** This is an event handler for the onClick event of buttonDispatch. */
    async buttonDispatchOnClick(event: ClickEvent) {
        const modelRow = this.table?.selectedRow?.data;
        if (modelRow == null)
            return;

        const button = event.target as Button;
        button.busy = true;
        if (modelRow.getBoolean("brokerage", false)) {
            const isBrokerageLtlOrder = modelRow.getBoolean("is_brokerage_ltl");
            const dispatchType = (await Api.post("lme/dispatch/movement-dispatch-type", { movement_id: modelRow.get("movement_id") })).data[0]?.["dispatch_type"];
            await (isBrokerageLtlOrder && StringUtil.equalsIgnoreCase(dispatchType, "P44") ? P44ShipmentTracking : DispatchSlideout).showSlideout(modelRow.get("movement_id"), {
                movementOrId: modelRow.get("movement_id"),
                orderId: modelRow.get("order_id"),
                customerId: modelRow.get("customer_id"),
                orderOnHold: modelRow.getBoolean("hold", false),
                originCity: modelRow.get("origin_city"),
                originState: modelRow.get("origin_state"),
                destinationCity: modelRow.get("destination_city"),
                destinationState: modelRow.get("destination_state"),
                manifestID: modelRow.get("manifest_id"),
                callinRequiredBrkStatusCode: this.table["callinRequiredBrkStatusCode"]
            }).finally(() => button.busy = false);
        } else {
            const equipmentId = getDispatchControlValue("dispatch_by") == "T" ? modelRow.get("tractor_number") : modelRow.get("driver1_id");
            const orderId = modelRow.get("order_id");
            Dispatch.openSlideout({
                equipmentId,
                orderId: orderId,
                slideoutTitle: "Dispatch",
                equipmentType: getDispatchControlValue("dispatch_by"),
            });
            button.busy = false;
        }
    }

    // Asset logic for determining if dispatch should be enabled for asset moves
    private async dispatchButtonEnabled(tableRow: TableRow): Promise<boolean> {
        const modelRow = tableRow?.data;
        if (this.buttonDispatch.visible === false || modelRow == null) {
            return false;
        }

        if (!GeneralSettings.hasEarlyAdoptersAccess()) {
            this.buttonDispatch.disabledTooltip = "Dispatch for asset movements is not available in this version";
            return false;
        }

        if (["D"].includes(modelRow.getString("move_status"))) {
            this.buttonDispatch.disabledTooltip = "This asset movement has already been delivered";
            return false;
        }
        if ((LMEGeneralSettings.getSingleton().getDispatchControl().prevent_dsp_hold === "Y") && (modelRow.getBoolean("hold", false))) {
            this.buttonDispatch.disabledTooltip = "Dispatch button is disabled because the order is on hold.";
            return false;
        }
        if (!modelRow.getBoolean("brokerage", false) && !GeneralSettings.hasEarlyAdoptersAccess()) {
            this.buttonDispatch.disabledTooltip = "Dispatch button is disabled for non early adopters."
            return false;
        }
        if (modelRow.isNull("tractor_number") || modelRow.isNull("driver1_id")) {
            this.buttonDispatch.disabledTooltip = "Missing tractor or driver";
            return false;
        }
        if (!modelRow.isNull("equip_group_id")) {
            const equipGroupId = modelRow.getString("equip_group_id");
            const equipmentGroupRow = await new ModelEquipmentGroup().searchSingle({ id: equipGroupId });
            const validationResult = await EquipmentGroupValidation.validate({
                modelRow: equipmentGroupRow,
                driverId: modelRow.get("driver1_id", null),
                driver2Id: modelRow.get("driver2_id", null),
                tractorId: modelRow.get("tractor_number", null),
                field: EquipmentGroupValidation.getDispatchByField()
            });
            this.validationResult = validationResult;
            const stopDispatchWarnings = validationResult.equipmentWarnings.filter(warning => warning.equipment_requirement == "A" || warning.equipment_requirement == "N");
            if (stopDispatchWarnings.length > 0) {
                this.buttonDispatch.disabledTooltip = "Equipment does not meet requirements. Issue required equipment or reassign this load to another Driver to enable Dispatch.";
                return false;
            }
            if (validationResult.driverEvents) {
                validationResult.driverEvents = validationResult.driverEvents.filter(event => EquipmentGroupValidation.driverEventDispatchFilter(event));
                const shipDate = modelRow.getDate("origin_early");
                if (validationResult.driverEvents?.find(row =>
                    !row.getBoolean("equipstatuscode.allow_dispatch", true)
                    && shipDate >= row.getDate("equipstatus.start_date")
                    && shipDate <= row.getDate("equipstatus.end_date")
                )) {
                    this.buttonDispatch.disabledTooltip = "There is a conflicting Driver Event that does not allow dispatching. Reassign this load to another Driver to enable Dispatch.";
                    return false;
                }
            }
        } else {
            this.validationResult = null;
        }

        return true;
    }

    buttonOrderHistoryOnClick(event: ClickEvent) {
        const modelRow = this.table?.selectedRow?.data;
        if (modelRow == null)
            return;

        const props: OrderPostHistProps = {
            orderType: "ORDER",
            orderId: modelRow.get("order_id"),
            movementId: modelRow.get("movement_id"),
            isBilled: !modelRow.isNull("bill_date") || !modelRow.isNull("transfer_to_billing_date")
        }
        OrderPostHist.showInSlideout(
            props, DataSourceMode.UPDATE, this.buttonOrderHistory);
    }

    buttonReleaseToBillingOnClick(event: ClickEvent) {
        const button = event.target as Button;
        const modelRow = this.table?.selectedRow?.data;
        if (modelRow == null) return;
        button.busy = true;
        const order_id = modelRow.get("order_id");
        Api.post("lme/powerbroker/release-to-billing", { order_id }).then(output => {
            const success = output.data[0].success;
            const error_message = output.data[0].error_message;
            if (success) {
                Toast.showSuccessToast("Released to Invoicing", "Order " + order_id);
            } else {
                Toast.showToast(error_message);
            }
        }).finally(() => {
            button.busy = false;
        });
    }

    async buttonOnHoldOnClick(event: ClickEvent) {
        const selectedRows = this.table.selectedRows;
        const isOnHold = selectedRows?.[0].data.getBoolean("hold");
        this.onHoldAction.execute({isOnHold, selectedRows});
    }

    buttonUpdateFieldOnClick(event: ClickEvent) {
        CommonContextMenuItems.buttonUpdateFieldOnClick(this.table.selectedRows, true);
    }

    buttonRateOrdersOnClick(event: ClickEvent) {
        CommonContextMenuItems.buttonRateOrdersOnClick(this.table.selectedRows);
    }

    static getContextMenuItems(toolPanel: CustomerServiceToolPanel, tableRow: TableRow): Partial<ContextMenuLabelProps>[] {
        let selectedRows = tableRow.table.selectedRows;
        if (!selectedRows.includes(tableRow)) {
            tableRow.table.selectedRow = tableRow;
            selectedRows = [tableRow]
        }

        if (selectedRows.length == 1) {
            return OrderTabView.addNavigationContextMenuItems(
                toolPanel.getContextMenuItems(selectedRows),
                selectedRows?.[0]?.data?.get("order_id")
            );
        } else {
            return toolPanel.getContextMenuItems(selectedRows);
        }
    }

    public setAsynchronousOptions(event: ContextMenuCreationEvent) {
        event.menuLabels.forEach(label => {
            if (label.text === "Dispatch") {
                this.buttonDispatchRightClick = label;
            }
        });
    }

    public getContextMenuItems(selectedRows: TableRow[]): Partial<ContextMenuLabelProps>[] {

        const isOnHold = selectedRows[0]?.data.getBoolean("hold");
        const contextItems = [
            ...ContextMenuItemCreator.fromButtons(
                { button: this.buttonDispatch, text: "Dispatch" },
                { button: this.buttonOrderHistory, text: "Order History" },
                { button: this.buttonReleaseToBilling, text: "Release to Invoicing" },
                { button: this.buttonHold, text: isOnHold ? "Remove Hold": "Apply Hold" }
            ),
            CommonContextMenuItems.createUpdateFieldItemFromSelected(selectedRows, true),
            CommonContextMenuItems.createRateOrdersItemFromSelected(selectedRows),
            CommonContextMenuItems.createRecentChangesItemFromSelected(selectedRows)
        ];

        if (contextItems.filter(item => item != null && item.visible).length > 0) {
            return contextItems;
        } else {
            return [{text: "No Available Multi-Select Actions"}];
        }
    }
}
